import type { ComponentType } from 'react';

import { Box, Divider, Portal, Slide, Stack, Typography } from '@mui/material';

import { tokens } from '../../../theme/generated/tokens';
import { DynamicGridDrawer } from './DynamicGridDrawer';
import { Paper } from '../../boxes';
import { Button } from '../../buttons';
import type { DrawerProps } from '../../drawer/types';
import { useDrawer } from '../../drawer/hooks';
import { useDynamicGridContext } from '../useDynamicGridContext';

export type DynamicGridActionBarDrawerProps = DrawerProps & {
  title?: string;
};

export type DynamicGridActionBarProps = {
  description?: string;
  drawerTitle?: string;
  /** Custom drawer opened by "Add cards". Defaults to DynamicGridDrawer wrapper. */
  drawerComponent?: ComponentType<DynamicGridActionBarDrawerProps>;
};

const DynamicGridCustomizeDrawer = ({
  title = 'Customize dashboard',
  ...props
}: DynamicGridActionBarDrawerProps) => {
  const { items, hiddenItems, toggleItemVisibility, resetLayout } =
    useDynamicGridContext();
  return (
    <DynamicGridDrawer
      items={items}
      hiddenItems={hiddenItems}
      toggleItemVisibility={toggleItemVisibility}
      onRestoreDefaults={resetLayout}
      title={title}
      {...props}
    />
  );
};

export const DynamicGridActionBar = ({
  description = 'Reorder, resize, remove or add cards',
  drawerTitle = 'Customize dashboard',
  drawerComponent = DynamicGridCustomizeDrawer,
}: DynamicGridActionBarProps) => {
  const { isCustomizing, saveChanges, discardChanges } =
    useDynamicGridContext();
  const { open } = useDrawer();

  const handleManageCards = () =>
    open({
      component: drawerComponent,
      props: { title: drawerTitle },
    });

  return (
    <Portal>
      <Box
        sx={{
          position: 'fixed',
          left: '50%',
          bottom: 32,
          transform: 'translateX(-50%)',
          zIndex: (theme) => theme.zIndex.modal,
        }}
      >
        <Slide direction="up" in={isCustomizing} mountOnEnter unmountOnExit>
          <Paper
            sx={{
              boxShadow: tokens.theme.shadows.elevation.medium,
              px: 20,
              py: 10,
              borderRadius: '14px',
              minWidth: 680,
              height: 56,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={12}
              sx={{ height: '100%' }}
            >
              <Typography
                variant="P14M"
                sx={{ color: tokens.theme.fg.structural.secondary, flex: 1 }}
              >
                {description}
              </Typography>

              <Button
                variant="secondary"
                size="sm"
                onClick={discardChanges}
                testId="discard-changes-button"
              >
                Discard
              </Button>

              <Divider orientation="vertical" flexItem sx={{ mx: 4 }} />

              <Button
                variant="secondary"
                size="sm"
                onClick={handleManageCards}
                testId="add-cards-button"
              >
                Add cards
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={saveChanges}
                testId="save-changes-button"
              >
                Save changes
              </Button>
            </Stack>
          </Paper>
        </Slide>
      </Box>
    </Portal>
  );
};
